import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { Doc, SharedDriveModel } from '../../../../../../../types';
import {
  getUserPermission,
  moveSharedDriveDocs,
  UserKeys,
} from '../../../../../../api/users-api';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import GeneralDialog from '../../../../../../shared/general-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { useStore } from '../../../../../state/zustand-store';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { isAbleToSeeUIElement } from '../../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import SharedDrivesChildMoveDialogContent from './shared-drives-child-move-dialog-content';
import { DriveRoles } from '../../../../../../utils/roles/client-roles';
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs';
import ActingAsUserMessage from '../../../../../../shared/acting-as-user-message';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

interface SharedDrivesChildMoveDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDocs: Doc[];
  setSelectedDocs: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  selectedFolder: string;
}

export default function SharedDrivesChildMoveDialog({
  open,
  handleClose,
  selectedDocs,
  setSelectedDocs,
  changeLogRoles,
  selectedFolder,
}: SharedDrivesChildMoveDialogProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const [autocompleteValue, setAutocompleteValue] = useState('');

  const { loggedInUser } = useUserSettings();

  const [selectedDrive, setSelectedDrive] = useState<SharedDriveModel | null>(
    null
  );
  const [selectedDrivePickerFolder, setSelectedDrivePickerFolder] =
    useState<CallbackDoc | null>(null);

  // just for this component
  const [openPermissionCheckDialog, setOpenPermissionCheckDialog] =
    useState(false);

  const [permissionCheckLoading, setPermissionCheckLoading] = useState(false);

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const { mutate, isPending: isMoving } = useMutation({
    mutationFn: (id: string) =>
      moveSharedDriveDocs(selectedDocs, id, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to move the documents or folders has started.',
      });
      setChangeLogMessage('');
      setSelectedDocs([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue moving the selected documents or folders. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.shared_drives.content.move_drive_content, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(
          analytics.drive.drive_explorer.shared_drives.content
            .move_drive_content,
          {
            module: ModuleList.modules.drive.name,
            section: ModuleList.modules.drive.sections.driveExplorer,
          }
        );
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives.content
            .move_drive_content,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  async function handleSubmit() {
    setPermissionCheckLoading(true);

    const isSharedDrive =
      selectedDrive && selectedDrivePickerFolder ? false : true;

    // check if the user has permission to move to the selected folder
    const hasPermission = await queryClient.fetchQuery({
      queryKey: [
        UserKeys.SharedDriveUserPermission,
        loggedInUser,
        isSharedDrive ? selectedDrive?.id : selectedDrivePickerFolder?.id,
      ],
      queryFn: () =>
        getUserPermission(
          selectedDrive && selectedDrivePickerFolder
            ? selectedDrivePickerFolder.id
            : selectedDrive
            ? selectedDrive.id
            : selectedFolder,
          loggedInUser,
          isSharedDrive
        ),
      retry: false,
      staleTime: 1000 * 60 * 2,
    });

    const allowedPermissions = ['organizer'];

    const hasCorrectPermission =
      hasPermission && allowedPermissions.includes(hasPermission);

    setPermissionCheckLoading(false);

    if (hasCorrectPermission) {
      // do normal action
      if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanMoveDocs.value)) {
        setOpenChangeLogDialog(true);
      } else {
        const id =
          selectedDrive && selectedDrivePickerFolder
            ? selectedDrivePickerFolder.id
            : selectedDrive
            ? selectedDrive.id
            : selectedFolder;
        mutate(id);
      }
    } else {
      setOpenPermissionCheckDialog(true);
    }
  }

  const isSameSharedDrive =
    selectedDrive?.id === selectedFolder && selectedDrivePickerFolder === null;

  const nothingSelected =
    selectedDrive === null && selectedDrivePickerFolder === null;

  const selectedPickerFolderIsInDocs = selectedDocs.some(
    (doc) => doc.id === selectedDrivePickerFolder?.id
  );

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Move Documents"
      dialogContent={
        <Stack>
          <Box sx={{ px: 4 }}>
            <ActingAsUserMessage userEmail={loggedInUser} />
          </Box>
          <SharedDrivesChildMoveDialogContent
            autocompleteValue={autocompleteValue}
            setAutocompleteValue={setAutocompleteValue}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            selectedDrivePickerFolder={selectedDrivePickerFolder}
            setSelectedDrivePickerFolder={setSelectedDrivePickerFolder}
            selectedFolder={selectedFolder}
            selectedPickerFolderIsInDocs={selectedPickerFolderIsInDocs}
            openChangeLogDialog={openChangeLogDialog}
            setOpenChangeLogDialog={setOpenChangeLogDialog}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            isMoving={isMoving}
            openPermissionCheckDialog={openPermissionCheckDialog}
            setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
            changeLogRoles={changeLogRoles}
            setPermissionCheckLoading={setPermissionCheckLoading}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Move',
        action: handleSubmit,
        loading: isMoving || permissionCheckLoading,
        disabled:
          isMoving ||
          permissionCheckLoading ||
          isSameSharedDrive ||
          nothingSelected ||
          selectedPickerFolderIsInDocs,
      }}
    />
  );
}
